import React, { FC, ReactElement, useMemo } from 'react';

import classNames from 'classnames';

import { EvSection } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { ErrorView } from 'src/apps/dashboard/components/error-view/ErrorView';
import { BUSINESS_UNIT_WEB_SDK_TABLE_ID } from 'src/apps/dashboard/constants/Dashboard';
import { useWebSdkTestsQuery } from 'src/apps/dashboard/hooks/useWebSdkTestsQuery';
import { getWebSdkTestsColumns } from 'src/apps/dashboard/pages/business-units/helpers/WebSdkTestColumns';
import { ERROR_TYPE } from 'src/apps/dashboard/types/chartErrors';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import './BusinessUnitWebSdkTable.scss';

interface IBusinessUnitWebSdkTableProps {
	dashboardFilters: IFilterOptions;
	showOnlyLastReport: boolean;
	unitIdLabel?: string;
	topRowActions?: ReactElement;
}

export const BusinessUnitWebSdkTable: FC<IBusinessUnitWebSdkTableProps> = ({
	dashboardFilters,
	showOnlyLastReport,
	unitIdLabel,
	topRowActions
}) => {
	const { getTableStateById } = useTableState();
	const tableState = getTableStateById(BUSINESS_UNIT_WEB_SDK_TABLE_ID);
	const { tableSort, paginationInfo } = tableState;

	const testPeriod = dashboardFilters?.dateRange
		? String(dashboardFilters.dateRange[0].value)
		: '30';
	const customFieldFilters: IFilterOptions = omit(dashboardFilters ?? {}, [
		'dateRange',
		'searchValue'
	]);
	customFieldFilters.unitId = [{ label: unitIdLabel, value: unitIdLabel }];

	const [testsQuery] = useWebSdkTestsQuery({
		showRecentTest: showOnlyLastReport,
		filters: customFieldFilters,
		dateRange: testPeriod,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage,
		showTestPath: false
	});

	const total = testsQuery.data?.total ?? 0;
	const unfilteredTotal = testsQuery.data?.unfilteredTotal ?? 0;

	const rows = (testsQuery.data?.rows ?? []).map((row) => ({
		...row,
		testDisplayName: row.testName
	}));

	const isTableLoading =
		testsQuery.isLoading ||
		testsQuery.isPreviousData ||
		(testsQuery.isStale && testsQuery.isRefetching);

	const tableOptions = useMemo(
		() => ({
			keyField: 'runId',
			title: 'Web SDK Tests',
			caption: 'Web SDK table',
			pagination: true,
			dataType: 'tests',
			remote: true
		}),
		[]
	);

	if (isTableLoading) {
		return (
			<EvSection
				ariaLabel="Web SDK Tests Table"
				skipLinkId="main-section"
				className={classNames('dashboard-section', 'table-section')}
			>
				<TableSkeleton columns={6} rowCount={5} />
			</EvSection>
		);
	}

	if (testsQuery.isError || (rows.length === 0 && !isTableLoading)) {
		const errorType = testsQuery.isError ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA;

		return <ErrorView errorType={errorType} />;
	}

	return (
		<EvSection
			ariaLabel="Web SDK Tests Table"
			skipLinkId="main-section"
			className={classNames('dashboard-section', 'table-section')}
		>
			<PlatformTable
				isTableLoading={isTableLoading}
				tableId={BUSINESS_UNIT_WEB_SDK_TABLE_ID}
				tableData={rows}
				totalPaginationableResults={total}
				totalCount={unfilteredTotal}
				className="business-unit-web-sdk-table"
				columns={getWebSdkTestsColumns()}
				topRowActions={topRowActions}
				options={tableOptions}
			/>
		</EvSection>
	);
};
